// API utility functions for better error handling

const BASE_URL = 'https://api.mariamlyluxe.com/public';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
};

const getAuthHeadersForFormData = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Accept': 'application/json'
  };
};

export const apiRequest = async (url, options = {}) => {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;
  
  try {
    const response = await fetch(fullUrl, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...options.headers
      }
    });

    // Check if response is HTML (error page)
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON response from:', fullUrl, text);
      throw new Error('Server returned an error page. Please check your API endpoint and server status.');
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP ${response.status}: Request failed`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', fullUrl, error);
    throw error;
  }
};

export const apiFormDataRequest = async (url, formData, options = {}) => {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;
  
  try {
    const response = await fetch(fullUrl, {
      method: 'POST',
      ...options,
      headers: {
        ...getAuthHeadersForFormData(),
        ...options.headers
      },
      body: formData
    });

    // Check if response is HTML (error page)
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON response from:', fullUrl, text);
      throw new Error('Server returned an error page. Please check your API endpoint and server status.');
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP ${response.status}: Request failed`);
    }

    return await response.json();
  } catch (error) {
    console.error('API form data request failed:', fullUrl, error);
    throw error;
  }
};

// Specific API functions for Product Variants
export const productVariantsAPI = {
  // Get all products
  getProducts: () => apiRequest('/api/products'),
  
  // Get single product with variants
  getProduct: (productId) => apiRequest(`/api/products/${productId}`),
  
  // Create product with variants
  createProduct: (formData) => apiFormDataRequest('/api/products', formData),
  
  // Add variant to existing product
  addVariant: (productId, formData) => apiFormDataRequest(`/api/products/${productId}/variants`, formData),
  
  // Update variant
  updateVariant: (variantId, formData) => apiFormDataRequest(`/api/variants/${variantId}`, formData),
  
  // Add images to variant
  addImagesToVariant: (variantId, formData) => apiFormDataRequest(`/api/variants/${variantId}/images`, formData),
  
  // Delete image from variant
  deleteImage: (variantId, imageId) => apiRequest(`/api/variants/${variantId}/images/${imageId}`, { method: 'DELETE' }),
  
  // Delete variant
  deleteVariant: (variantId) => apiRequest(`/api/variants/${variantId}`, { method: 'DELETE' }),
  
  // Get categories
  getCategories: () => apiRequest('/api/categories'),
  
  // Get brands
  getBrands: () => apiRequest('/api/brands')
};

// Offer Management API functions
export const offerAPI = {
  // Get current offer (public endpoint - no auth required)
  getCurrentOffer: () => {
    const fullUrl = `${BASE_URL}/api/offer`;
    return fetch(fullUrl, {
      headers: {
        'Accept': 'application/json'
      }
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Request failed`);
      }
      return await response.json();
    });
  },

  // Admin: Get offer details
  getOfferDetails: () => apiRequest('/api/admin/offer'),

  // Admin: Create or update offer
  saveOffer: (offerText) => apiRequest('/api/admin/offer', {
    method: 'POST',
    body: JSON.stringify({ offer_text: offerText })
  }),

  // Admin: Delete offer
  deleteOffer: () => apiRequest('/api/admin/offer', {
    method: 'DELETE'
  })
};

// Offer Sections Management API functions
export const offerSectionsAPI = {
  // Get all offer sections (admin)
  getOfferSections: () => apiRequest('/api/admin/offer-sections'),

  // Get specific offer section
  getOfferSection: (id) => apiRequest(`/api/admin/offer-sections/${id}`),

  // Create offer section
  createOfferSection: (formData) => apiFormDataRequest('/api/admin/offer-sections', formData),

  // Update offer section
  updateOfferSection: (id, formData) => {
    formData.append('_method', 'PUT');
    return apiFormDataRequest(`/api/admin/offer-sections/${id}`, formData, { method: 'POST' });
  },

  // Delete offer section
  deleteOfferSection: (id) => apiRequest(`/api/admin/offer-sections/${id}`, { method: 'DELETE' }),

  // Reorder offer sections
  reorderOfferSections: (ids) => apiRequest('/api/admin/offer-sections/reorder', {
    method: 'POST',
    body: JSON.stringify({ ids })
  }),

  // Get active offer sections (public endpoint - no auth required)
  getActiveOfferSections: () => {
    const fullUrl = `${BASE_URL}/api/offer-sections`;
    return fetch(fullUrl, {
      headers: {
        'Accept': 'application/json'
      }
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Request failed`);
      }
      return await response.json();
    });
  }
};

export default {
  apiRequest,
  apiFormDataRequest,
  productVariantsAPI,
  offerAPI,
  offerSectionsAPI
};
